import { EventTags, OptimizelyExperiment, UserAttributes } from '@optimizely/optimizely-sdk'
import { WithOptimizelyProps as OptimizelyProps, withOptimizely } from '@optimizely/react-sdk'
import * as Sentry from '@sentry/browser'
import { compose, withHandlers, withProps } from 'recompose'

import { OPTIMIZELY_CONTROL_EXPERIMENT, OPTIMIZELY_FEATURE_FLAGS } from 'app-constants'
import platform from 'components/Trackers/platform'
import Environment, { withEnv } from 'utils/environment'
import { getFlow } from 'utils/FlowProvider/utils'
import { setVisitorId } from 'utils/setVisitorId'
import removeDuplicateQueryParams from 'utils/sourceAttributes'

import { getVariation } from './variationsUtils'

type Props = {
  env: Environment
  metricName: string
  experimentName: string
  featureName: string
} & OptimizelyProps

type HandlerProps = {
  trackOptimizely: (
    metricName: string,
    overrideUserId?: string | EventTags,
    overrideAttributes?: UserAttributes,
  ) => void
  activateOptimizely: (experimentName: string) => Promise<string>
  getVariation: (experimentName: string) => string
  isFeatureEnabled: (
    featureName: string,
    overrideUserId?: string,
    overrideUserAttributes?: any,
  ) => boolean
  getFeatureVariables: (featureName: string) => object
  forceVariation: (featureName: string, variationName: string) => void
  getOptimizelyExperimentConfig: (experimentName: string) => OptimizelyExperiment | null
}

export type WithOptimizelyProps = HandlerProps

export const getFeature = key => {
  const features = JSON.parse(window.sessionStorage.getItem(OPTIMIZELY_FEATURE_FLAGS))
  if (features) {
    return features[key]?.isFeatureEnabled ?? false
  }
  return false
}

const getOptimizelyTrackArgs = env => ({
  environment: env.get('optimizelyEnvironment'),
  platform,
  visitorId: setVisitorId(),
  flow: getFlow(),
})

export const enhance = compose<Props, {}>(
  withEnv,
  withOptimizely,
  withHandlers<Props, HandlerProps>({
    trackOptimizely: ({ optimizely, env }) => async (
      metricName: string,
      overrideUserId?: string | EventTags,
      overrideAttributes?: UserAttributes,
    ) => {
      try {
        // NOTE: dataReadyPromise is a private member of the SDK; refactor to avoid usage
        await optimizely.dataReadyPromise

        const userId = overrideUserId || setVisitorId()
        const args = overrideAttributes || getOptimizelyTrackArgs(env)
        optimizely.track(metricName, userId, args)
        /* istanbul ignore else */
        if (window.DD_LOGS) {
          window.DD_LOGS.logger.log(metricName, args)
        }
      } catch (e) {
        Sentry.addBreadcrumb({
          message: 'Failed to track optimizely metric: ' + metricName,
        })
        Sentry.captureException(e)
      }
    },
    activateOptimizely: ({ optimizely, env }) => async (experimentName: string) => {
      try {
        // NOTE: dataReadyPromise is a private member of the SDK; refactor to avoid usage
        await optimizely.dataReadyPromise
        return optimizely.activate(experimentName, setVisitorId(), {
          environment: env.get('optimizelyEnvironment'),
          platform,
          visitorId: setVisitorId(),
          flow: getFlow(),
        })
      } catch (e) {
        Sentry.addBreadcrumb({
          message: 'Failed to activate optimizely experiment: ' + experimentName,
        })
        Sentry.captureException(e)

        return OPTIMIZELY_CONTROL_EXPERIMENT
      }
    },
    getVariation: () => (experimentName: string) => {
      try {
        return getVariation(experimentName)
      } catch (e) {
        Sentry.addBreadcrumb({
          message: 'Failed to get variation for experiment: ' + experimentName,
        })
        Sentry.captureException(e)

        return OPTIMIZELY_CONTROL_EXPERIMENT
      }
    },
    isFeatureEnabled: ({ optimizely, env }) => (
      featureName: string,
      overrideUserId?: string,
      overrideAttributes?: UserAttributes,
    ) => {
      const params = removeDuplicateQueryParams(location.search)
      // In dev and staging, this allows for us to override the feature flag status.
      if (env.get('buildEnv') !== 'production' && featureName === 'tiered_registration') {
        if (params?.flow === 'tiered') {
          return true
        } else if (params?.flow === 'core' || getFlow() === 'core') {
          return false
        }
      }
      try {
        return optimizely.isFeatureEnabled(featureName, overrideUserId, overrideAttributes)
      } catch (e) {
        Sentry.addBreadcrumb({
          message: 'Failed to get featureFlag for experiment: ' + featureName,
        })
        Sentry.captureException(e)

        return false
      }
    },
    getFeatureVariables: ({ optimizely }) => (featureName: string) => {
      try {
        return optimizely.getFeatureVariables(featureName)
      } catch (e) {
        Sentry.addBreadcrumb({
          message: 'Failed to get feature variables for feature: ' + featureName,
        })
        Sentry.captureException(e)

        return null
      }
    },
    forceVariation: ({ optimizely }) => (experimentName: string, variationName: string) => {
      try {
        return optimizely.setForcedVariation(experimentName, setVisitorId(), variationName)
      } catch (e) {
        Sentry.addBreadcrumb({
          message: `Failed to force a user into an Optimizely variation: ': ${experimentName}: ${variationName}`,
        })
        Sentry.captureException(e)

        return false
      }
    },
    getOptimizelyExperimentConfig: ({ optimizely }) => (
      experimentName: string,
    ): OptimizelyExperiment | null => {
      const experiment = optimizely.getOptimizelyConfig().experimentsMap[experimentName]
      return experiment || null
    },
  }),
  withProps(({ optimizely }) => {
    return {
      optimizely,
    }
  }),
)

export { enhance as withOptimizely }
